import {
  applyRateWindow,
  computeRecs,
  kRateWindow,
  pRateWindow,
  type RecInputs,
} from "./fertility";

// Measured cost: ~58 ms for a 13-iteration solve on a 15,000-cell field, so it runs inline on a
// keystroke like every other control in the panel.

/* * Deficit sensitivity, lb of field average per ppm of critical — sets the iteration count we need. */
const PPM_SENSITIVITY = 8;

export interface BudgetSolution {
  /* * the resolved critical (K: the CEC>5 one; the low tier moves with it by the same delta) */
  critical: number;
  /* * what the field average ACTUALLY comes to — never assume it equals the request */
  achieved: number;
  /* * what stopped it reaching the target, when it could not */
  binding: "min" | "max" | "range" | null;
}

export interface BudgetInputs {
  soil: RecInputs;
  nutrient: "p2o5" | "k2o";
  /*
   *
   * K only: keep the CEC≤5 critical this far BELOW the high one — shift both by the same delta.
   */
  tierGap: number;
}

/*
 *
 * The rate window READ OFF THE SOIL INPUTS rather than passed alongside them. The first version
 * took `minLbAc`/`maxLbAc` as separate fields while `soil` already carried them, so the two could
 * disagree — and in the first test they did, reporting `binding: null` for a solve the floor was
 * plainly holding.
 */
function boundsOf(inp: BudgetInputs): {
  min: number;
  max: number;
  drop: number;
} {
  return inp.nutrient === "p2o5"
    ? {
        min: inp.soil.pMinLbAc ?? 0,
        max: inp.soil.pMaxLbAc ?? 0,
        drop: inp.soil.pDropBelowLbAc ?? 0,
      }
    : {
        min: inp.soil.kMinLbAc ?? 0,
        max: inp.soil.kMaxLbAc ?? 0,
        drop: inp.soil.kDropBelowLbAc ?? 0,
      };
}

const CRIT_MAX = { p2o5: 200, k2o: 400 } as const;

/* * Field average of the nutrient's rate, over cells that HAVE a rate (absent ≠ 0, here as everywhere). */
export function averageRate(soil: RecInputs, nutrient: "p2o5" | "k2o"): number {
  const recs = computeRecs(soil);
  const raw = nutrient === "p2o5" ? recs.pRaw : recs.kRaw;
  const window = nutrient === "p2o5" ? pRateWindow(soil) : kRateWindow(soil);
  // Maintenance and the P credit are already inside the unrounded rate. The average
  // is that final rate after the same window the map uses, so a binding max is the cap
  // on the total rather than on the pre-adjustment buildup.
  const arr = (raw ?? []).map((value) =>
    value == null || !Number.isFinite(value)
      ? null
      : Math.round(applyRateWindow(value, window)),
  );
  let sum = 0;
  let n = 0;
  for (const v of arr) {
    if (v == null || !Number.isFinite(v)) continue;
    sum += v;
    n++;
  }
  return n ? sum / n : 0;
}

/* * Apply a candidate critical to the soil inputs — the only place the P/K difference lives. */
function withCritical(inp: BudgetInputs, critical: number): RecInputs {
  return inp.nutrient === "p2o5"
    ? { ...inp.soil, pCritCustom: critical }
    : // BOTH tiers shift by the same delta: the CEC tiering reflects a real soil.
      // difference, so the gap between them is preserved rather than scaled away.
      {
        ...inp.soil,
        kCritHighCec: critical,
        kCritLowCec: Math.max(0, critical - inp.tierGap),
      };
}

/*
 *
 * Solve for the critical level whose field average is `targetAvg`.
 *
 * Returns the closest achievable answer and NAMES what bound it when that is not the request — the caller
 * must show the achieved figure rather than echoing back what was asked for.
 */
export function solveCriticalForBudget(
  inp: BudgetInputs,
  targetAvg: number,
): BudgetSolution {
  const hiBound: number = CRIT_MAX[inp.nutrient];
  const at = (c: number) => averageRate(withCritical(inp, c), inp.nutrient);

  const { min, max, drop } = boundsOf(inp);
  const lowest = at(0);
  const highest = at(hiBound);

  // Below the floor the average simply cannot go: with a blanket minimum every cell WITH DATA already
  // receives it, so no critical can average less. Naming `min` vs the generic `range` matters because the
  // user can act on it — the rate window is right there in the same panel. A drop makes 0 legal, so
  // the floor is not what stopped the solve.
  if (targetAvg <= lowest) {
    return {
      critical: 0,
      achieved: lowest,
      binding:
        drop > 0 ? "range" : min > 0 && lowest <= min + 0.5 ? "min" : "range",
    };
  }
  if (targetAvg >= highest) {
    return {
      critical: hiBound,
      achieved: highest,
      binding: max > 0 && highest >= max - 0.5 ? "max" : "range",
    };
  }

  // Iterations from the precision actually needed, not a round number: at ~8 lb of average per ppm, half a
  // pound of accuracy is ~0.06 ppm, and log2(400 / 0.06) ≈ 13. Forty would resolve to a ten-thousandth of
  // a ppm against a rate that rounds to whole pounds.
  const iters = Math.ceil(Math.log2(hiBound / (0.5 / PPM_SENSITIVITY)));
  let lo = 0;
  let hi = hiBound;
  for (let i = 0; i < iters; i++) {
    const mid = (lo + hi) / 2;
    if (at(mid) < targetAvg) lo = mid;
    else hi = mid;
  }
  const critical = Math.round(((lo + hi) / 2) * 10) / 10;
  return { critical, achieved: at(critical), binding: null };
}

/* * Total product for the whole field — growers order in pounds, not pounds per acre. */
export function totalForField(
  avgLbAc: number,
  acres: number | null | undefined,
): number | null {
  return Number.isFinite(avgLbAc) &&
    Number.isFinite(acres ?? NaN) &&
    (acres ?? 0) > 0
    ? Math.round(avgLbAc * (acres as number))
    : null;
}
